import routes from "./routes";
import { createRouter, createWebHistory } from "vue-router";
import { useUser } from "@/store/user";
import { keycloak } from "@/utilities/keycloak";
import { LocalStorage } from "@/storage";
import { intersection } from "lodash";
import { useAccount } from "@/store/account";

let isRedirect = false;

const router = createRouter({
  history: createWebHistory('/account/'),
  routes,
});

router.beforeEach(async (to, from) => {
  const userStore = useUser();
  const accountStore = useAccount();

  // Protected routes: redirect to public-home when not authenticated (user always sees public-home first)
  if (to.meta.authRequired && to.name !== "login" && to.name !== "public-home") {
    if (!userStore.isLoggedIn) {
      let authenticated = false;
      if (LocalStorage.getOr(["auth", "token"], false)) {
        keycloak.token = LocalStorage.getOr(["auth", "token"], undefined);
        keycloak.refreshToken = LocalStorage.getOr(
          ["auth", "refreshToken"],
          undefined
        );
        keycloak.idToken = LocalStorage.getOr(["auth", "idToken"], undefined);
        try {
          await keycloak.updateToken(10);
          authenticated = keycloak.authenticated;
        } catch {
          LocalStorage.removeKey(["auth", "token"]);
          LocalStorage.removeKey(["auth", "refreshToken"]);
          authenticated = false;
        }
      }
      if (!authenticated) {
        const query = {
          redirect: "true",
          name: to.name,
          // preserved for legacy query-string compat; no route currently sets params.role
          ...(to.params.role && { role: to.params.role }),
        };
        return { name: "public-home", query };
      }
      await Promise.all([userStore.getCurrent(), userStore.getRoles()]);
    }
  }

  // Direct /login when not authenticated → show public-home (same query so redirect is preserved)
  if (to.name === "login" && !userStore.isLoggedIn && !keycloak.authenticated) {
    return { name: "public-home", query: { ...to.query } };
  }

  // for TEST comment the condition below
  // (at the moment it's impossibile to use HD-Account directly to subscribe or view user profile)
  // ----------------------------------------
  if (!to.meta.alwaysAvailable && !accountStore.isExternalPlatformOrigin) {
    //the call comes from HDA application, return to home
    router.push({ name: "placeholder" });
    return;
  }
  // ----------------------------------------

  // Block FinalUser registration when account/init returns disableFinal: true.
  // Covers legacy /finaluser and the new data-driven route when its pageId
  // resolves to the FinalUser configuration. The /subscribe redirect lands
  // directly on `registration` — Vue Router runs guards only on the redirect
  // target, so no separate `subscribeFU` arm is needed.
  const isFinalUserRegistration =
    to.name === "finaluser" ||
    (to.name === "registration" && to.params.pageId === "register-finaluser");
  if (accountStore.current.disableFinal && isFinalUserRegistration) {
    return { name: "public-home" };
  }

  // After login (Keycloak callback): never show Login page when user is authenticated; go straight to My Services
  if (to.name === "login" && userStore.isLoggedIn) {
    return { name: "my-services" };
  }

  // Landed after Keycloak redirect (/, home, public-home or login): Keycloak has session but store not populated yet → load user then redirect
  const isCallbackRoute = to.path === "/" || to.name === "home" || to.name === "public-home" || to.name === "login";
  if (isCallbackRoute && keycloak.authenticated && !userStore.isLoggedIn) {
    try {
      userStore.setTokens(keycloak);
      await Promise.all([userStore.getCurrent(), userStore.getRoles()]);
      const stored = typeof sessionStorage !== "undefined" && sessionStorage.getItem("account.redirectAfterLogin");
      if (stored) {
        try {
          const { name: targetName, params = {} } = JSON.parse(stored);
          sessionStorage.removeItem("account.redirectAfterLogin");
          if (targetName && targetName !== "home") {
            return { name: targetName, params };
          }
        } catch {
          sessionStorage.removeItem("account.redirectAfterLogin");
        }
      }
      return { name: "my-services" };
    } catch (err) {
      console.warn("[Account] Post-login load user failed, redirecting to my-services anyway:", err);
      return { name: "my-services" };
    }
  }

  if (userStore.isSuspended) {
    if (to.meta.authRequired) {
      if (to.name !== "profile-suspended") {
        return router.push({
          name: "profile-suspended",
        });
      }
    }
  }

  // Redirect the user whenever it has incomplete roles to be completed.
  // Data-driven: uses role.page provided by /profile/init (getPageForPendingRoles in service-profile).
  if (!isRedirect && userStore.nextIncompleteRole?.page && to.name !== "dev") {
    isRedirect = true;
    return { name: "registration", params: { pageId: userStore.nextIncompleteRole.page } };
  }

  // Redirect logged-in users away from guest-only routes to My Services (default authenticated page)
  if (to.meta.guestOnly && userStore.isLoggedIn) {
    return { name: "my-services" };
  }

  // When routes are bound by role, ensure the user has at least one of the allowed roles
  if (to.meta.authRequired && to.meta.roles) {
    const matching = intersection(
      userStore.roles.map((r) => r.roleName),
      to.meta.roles
    );
    if (!matching.length) {
      return router.push({ name: "home" });
    }
  }
});

export default router;
